import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Leaf, Download, Copy, Loader2, Film, MessageCircle, Facebook, Instagram, Music2, ScrollText, Printer, FileText,
} from "lucide-react";
import { api, apiError, BACKEND_URL, downloadFile } from "../../lib/api";

const NETWORKS = [
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, cls: "bg-green-600 hover:bg-green-500 text-white" },
  { id: "facebook", label: "Facebook", icon: Facebook, cls: "bg-[#1877F2] hover:bg-[#3b8bf5] text-white" },
  { id: "instagram", label: "Instagram", icon: Instagram, cls: "bg-gradient-to-r from-pink-600 to-purple-600 hover:opacity-90 text-white" },
  { id: "tiktok", label: "TikTok", icon: Music2, cls: "bg-white hover:bg-zinc-200 text-black" },
];

const MaterialCard = ({ m, link }) => {
  const [showScript, setShowScript] = useState(false);
  const isProduct = m.category === "producto";
  const fullCaption = (m.caption || "") + (link ? `\n\n👉 ${isProduct ? "Conócelo y pídelo aquí" : "Conoce más y regístrate aquí"}: ${link}` : "");

  const copyCaption = async () => {
    await navigator.clipboard.writeText(fullCaption);
    toast.success("Texto copiado (incluye tu link personal)");
  };

  const publish = async (network) => {
    const caption = fullCaption;
    if (network === "whatsapp") {
      if (m.image_url) downloadFile(`${BACKEND_URL}${m.image_url}`, `renalife-${m.material_id}.jpg`);
      window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank");
      toast.success("WhatsApp abierto con tu texto. Adjunta la imagen descargada y envía.");
      return;
    }
    try { await navigator.clipboard.writeText(caption); } catch (err) { console.warn("clipboard:", err); }
    if (network === "tiktok") {
      if (m.video_url) downloadFile(m.video_url, `renalife-${m.material_id}.mp4`);
      else if (m.image_url) downloadFile(`${BACKEND_URL}${m.image_url}`, `renalife-${m.material_id}.jpg`);
      window.open("https://www.tiktok.com/upload", "_blank");
    } else {
      if (m.image_url) downloadFile(`${BACKEND_URL}${m.image_url}`, `renalife-${m.material_id}.jpg`);
      window.open(network === "facebook" ? "https://www.facebook.com/" : "https://www.instagram.com/", "_blank");
    }
    toast.success("Texto copiado y archivo descargado. Pega tu texto, sube el archivo y publica.");
  };

  return (
    <div data-testid={`renalife-material-${m.material_id}`} className="rounded-2xl border border-[#FACC15]/25 overflow-hidden" style={{ background: "linear-gradient(160deg, #04231b 0%, #032018 100%)" }}>
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-2 text-[#FACC15]">
          <Leaf size={18} />
          <h2 className="font-renalife font-bold text-2xl text-[#F8FAF8]">{m.title}</h2>
        </div>
        <p className="text-sm text-emerald-100/80 mt-2 rounded-xl border border-emerald-500/25 bg-emerald-500/5 px-4 py-3">
          <span className="font-semibold text-emerald-300">{isProduct ? "Beneficios: " : "Cómo funciona este bono: "}</span>{m.business_rule}
        </p>
        {isProduct && (
          <p className="text-[11px] text-zinc-500 mt-2">Este producto no es un medicamento.</p>
        )}

        <div className="grid md:grid-cols-2 gap-5 mt-6">
          {/* Imagen */}
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-200/60 font-mono mb-2">Imagen lista</div>
            {m.image_url ? (
              <div className="rounded-xl overflow-hidden border border-white/10">
                <img data-testid="material-image" src={`${BACKEND_URL}${m.image_url}`} alt={m.title} className="w-full object-cover" />
                <button onClick={() => downloadFile(`${BACKEND_URL}${m.image_url}`, `renalife-${m.material_id}.jpg`)} className="w-full flex items-center justify-center gap-1.5 text-xs py-2.5 bg-black/40 hover:bg-[#FACC15] hover:text-[#022C22] font-semibold transition-colors">
                  <Download size={14} /> Descargar imagen
                </button>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-zinc-500">
                {isProduct ? "Imagen en preparación (esperando la foto real del producto)…" : "Imagen en preparación…"}
              </div>
            )}
          </div>

          {/* Video */}
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-200/60 font-mono mb-2">Video listo</div>
            {m.video_url ? (
              <div className="rounded-xl overflow-hidden border border-white/10">
                <video data-testid="material-video" src={m.video_url} controls className="w-full max-h-80 bg-black" />
                <button onClick={() => downloadFile(m.video_url, `renalife-${m.material_id}.mp4`)} className="w-full flex items-center justify-center gap-1.5 text-xs py-2.5 bg-black/40 hover:bg-[#FACC15] hover:text-[#022C22] font-semibold transition-colors">
                  <Download size={14} /> Descargar video
                </button>
              </div>
            ) : m.video_status === "processing" ? (
              <div className="rounded-xl border border-dashed border-[#FACC15]/30 p-8 text-center text-sm text-emerald-200/80">
                <Loader2 className="animate-spin mx-auto mb-2 text-[#FACC15]" size={20} /> Generando tu video…
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-zinc-500"><Film className="mx-auto mb-2" size={20} /> Video en preparación…</div>
            )}
            <button data-testid="toggle-script" onClick={() => setShowScript((v) => !v)} className="mt-2 inline-flex items-center gap-1.5 text-xs text-emerald-300/80 hover:text-emerald-200">
              <ScrollText size={13} /> {showScript ? "Ocultar guion completo" : "Ver guion completo del video"}
            </button>
            {showScript && (
              <pre className="mt-2 whitespace-pre-wrap text-[11px] leading-relaxed text-emerald-100/70 bg-black/30 border border-white/10 rounded-xl p-4 font-mono">{m.video_script}</pre>
            )}
          </div>
        </div>

        {/* Texto */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-emerald-200/60 font-mono">Texto listo para publicar</div>
            {m.caption && (
              <button data-testid="copy-caption" onClick={copyCaption} className="inline-flex items-center gap-1 text-xs text-[#FACC15] hover:text-yellow-300 font-semibold"><Copy size={13} /> Copiar</button>
            )}
          </div>
          {m.caption ? (
            <p data-testid="material-caption" className="text-sm text-zinc-200 whitespace-pre-wrap rounded-xl bg-black/30 border border-white/10 p-4">{m.caption}</p>
          ) : (
            <div className="rounded-xl border border-dashed border-white/15 p-5 text-center text-sm text-zinc-500">Texto en preparación…</div>
          )}
        </div>

        {/* Publicar */}
        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-emerald-200/60 font-mono mb-3">Publica con un toque en:</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {NETWORKS.map((n) => (
              <button
                key={n.id}
                data-testid={`publish-${n.id}`}
                onClick={() => publish(n.id)}
                className={`flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all hover:-translate-y-0.5 ${n.cls}`}
              >
                <n.icon size={16} /> {n.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-zinc-500 mt-3">
            WhatsApp se abre con tu texto listo (adjunta la imagen descargada). En Facebook/Instagram/TikTok tu texto queda copiado y el archivo descargado: solo pega, sube y publica.
          </p>
        </div>
      </div>
    </div>
  );
};

export default function RenalifeMaterials() {
  const [materials, setMaterials] = useState(null);
  const [slug, setSlug] = useState(null);
  const [pdfs, setPdfs] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/renalife/my-landing").then((r) => setSlug(r.data.slug)).catch((err) => console.warn("my-landing:", err));
    api.get("/renalife/pdfs").then((r) => setPdfs(r.data)).catch((err) => console.warn("pdfs:", err));
  }, []);

  const bonos = useMemo(() => (materials || []).filter((m) => (m.category || "bono") === "bono"), [materials]);
  const productos = useMemo(() => (materials || []).filter((m) => m.category === "producto"), [materials]);

  const linkFor = (materialId) => {
    if (!slug) return null;
    const base = `${window.location.origin}/r/${slug}`;
    if (materialId === "bono2_matching") return `${base}/matching`;
    if (materialId === "bono3_infinito") return `${base}/infinito`;
    if (materialId === "bono4_fundador") return `${base}/fundador`;
    if (materialId.startsWith("prod_")) return `${base}/producto/${materialId}`;
    return base;
  };

  const copyLink = async (url) => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/renalife/materials");
      setMaterials(data);
      if (data.some((m) => m.video_status === "processing")) {
        clearTimeout(timer.current);
        timer.current = setTimeout(load, 8000);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
    return () => clearTimeout(timer.current);
  }, [load]);

  if (!materials) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#FACC15]" size={30} /></div>;
  }

  return (
    <div data-testid="renalife-materials-page" className="space-y-8">
      <div>
        <h1 className="font-renalife font-bold text-3xl sm:text-4xl text-[#F8FAF8]">
          Materiales <span className="text-[#FACC15]">RENALIFE</span>
        </h1>
        <p className="text-sm text-emerald-100/70 mt-2 max-w-2xl">
          Diseños oficiales listos para publicar. No tienes que crear nada: elige la red social y publica con un toque.
        </p>
      </div>

      {slug && materials && (
        <div data-testid="renalife-links-card" className="rounded-2xl border border-[#FACC15]/30 p-6" style={{ background: "linear-gradient(135deg, #022C22, #064E3B)" }}>
          <h2 className="font-renalife font-bold text-xl text-[#F8FAF8]">Tus páginas personales <span className="text-[#FACC15]">con link único</span></h2>
          <p className="text-xs text-emerald-100/70 mt-1 mb-4">Cada persona que entre y presione el botón llegará a TU WhatsApp y quedará registrada en tus Prospectos.</p>
          {[
            { key: "bono", header: "Oportunidad de negocio · Bonos" },
            { key: "producto", header: "Productos" },
          ].map((group) => (
            <div key={group.key} className="mb-4 last:mb-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FACC15]/70 mb-2">{group.header}</div>
              <div className="space-y-2">
                {(group.key === "bono" ? bonos : productos).map((m) => {
                  const url = linkFor(m.material_id);
                  return (
                    <div key={m.material_id} className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl bg-black/30 border border-white/10 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-emerald-200">{m.title}</div>
                        <div className="text-[11px] font-mono text-zinc-400 truncate">{url}</div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button data-testid={`copy-link-${m.material_id}`} onClick={() => copyLink(url)} className="inline-flex items-center gap-1 text-xs font-bold rounded-lg px-3 py-1.5 bg-[#FACC15] text-[#022C22] hover:bg-yellow-300"><Copy size={12} /> Copiar</button>
                        <a data-testid={`view-link-${m.material_id}`} href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">Ver</a>
                        <a data-testid={`wa-link-${m.material_id}`} href={`https://wa.me/?text=${encodeURIComponent(`Mira esto 👀 ${url}`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 bg-green-600 text-white hover:bg-green-500"><MessageCircle size={12} /> WhatsApp</a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <h2 data-testid="section-bonos" className="font-renalife font-bold text-2xl text-[#F8FAF8] mb-1">
          Oportunidad de negocio <span className="text-[#FACC15]">· Bonos</span>
        </h2>
        <p className="text-xs text-emerald-100/60 mb-5">Materiales para reclutar y hacer crecer tu red.</p>
        <div className="space-y-8">
          {bonos.map((m) => <MaterialCard key={m.material_id} m={m} link={linkFor(m.material_id)} />)}
        </div>
      </div>

      <div>
        <h2 data-testid="section-productos" className="font-renalife font-bold text-2xl text-[#F8FAF8] mb-1">
          Información para publicar <span className="text-[#FACC15]">PRODUCTOS</span>
        </h2>
        <p className="text-xs text-emerald-100/60 mb-5">
          Materiales con los beneficios de cada producto, enfocados a VENDER. Recuerda: "Este producto no es un medicamento".
        </p>
        <div className="space-y-8">
          {productos.map((m) => <MaterialCard key={m.material_id} m={m} link={linkFor(m.material_id)} />)}
        </div>
      </div>

      <div>
        <h2 data-testid="section-folletos" className="font-renalife font-bold text-2xl text-[#F8FAF8] mb-1">
          Herramientas para imprimir <span className="text-[#FACC15]">FOLLETOS</span>
        </h2>
        <p className="text-xs text-emerald-100/60 mb-5">
          PDFs oficiales listos para descargar e imprimir: plan de compensación, folleto de cada producto y la guía educativa de salud renal.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {pdfs.map((p) => (
            <div key={p.doc_id} data-testid={`pdf-card-${p.doc_id}`} className="flex items-center justify-between gap-3 rounded-xl border border-[#FACC15]/25 bg-[#04231b] px-4 py-3.5">
              <div className="flex items-center gap-3 min-w-0">
                {p.kind === "plan" ? <Printer size={18} className="text-[#FACC15] shrink-0" /> : <FileText size={18} className="text-emerald-400 shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-100 truncate">{p.title}</div>
                  <div className="text-[10px] font-mono text-zinc-500 uppercase">{p.kind}</div>
                </div>
              </div>
              {p.ready ? (
                <a
                  data-testid={`pdf-download-${p.doc_id}`}
                  href={`${BACKEND_URL}/api/public/renalife-pdf/${p.doc_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold rounded-lg px-3 py-2 bg-[#FACC15] text-[#022C22] hover:bg-yellow-300 shrink-0"
                >
                  <Download size={13} /> PDF
                </a>
              ) : (
                <span className="text-[11px] text-zinc-500 shrink-0">En preparación…</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
